export interface EncryptedData {
  data: string;
  nonce: string;
}

export class LocalStorageEncryptionService {
  private static async getKeyFromBase64(base64Key: string): Promise<CryptoKey> {
    const keyBuffer = Uint8Array.from(atob(base64Key), c => c.charCodeAt(0));
    return await crypto.subtle.importKey(
      'raw',
      keyBuffer,
      { name: 'AES-GCM' },
      false,
      ['encrypt', 'decrypt']
    );
  }

  static async encrypt(data: string, key: string): Promise<EncryptedData> {
    const cryptoKey = await this.getKeyFromBase64(key);
    const dataBuffer = new TextEncoder().encode(data);
    
    const nonce = crypto.getRandomValues(new Uint8Array(12));
    const encryptedBuffer = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: nonce,
      },
      cryptoKey,
      dataBuffer
    );

    return {
      data: btoa(String.fromCharCode(...new Uint8Array(encryptedBuffer))),
      nonce: btoa(String.fromCharCode(...nonce))
    };
  }

  static async decrypt(encryptedData: EncryptedData, key: string): Promise<string> {
    const cryptoKey = await this.getKeyFromBase64(key);
    const encryptedBuffer = Uint8Array.from(atob(encryptedData.data), c => c.charCodeAt(0));
    const nonce = Uint8Array.from(atob(encryptedData.nonce), c => c.charCodeAt(0));
    
    try {
      const decryptedBuffer = await crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: nonce,
        },
        cryptoKey,
        encryptedBuffer
      );

      return new TextDecoder().decode(decryptedBuffer);
    } catch (error) {
      throw new Error('Failed to decrypt data');
    }
  }
}
